import ActivityLogApi, {
  ActivityLogBatchPayload,
  ActivityLogEventPayload,
} from "../services/ActivityLogApi";

/**
 * Coletor do log de atividade: acumula eventos e descarrega em lote.
 *
 * Módulo singleton (e não hook/contexto) para que qualquer handler possa chamar
 * `trackActivity()` sem prop drilling nem re-render — o coletor não tem nada a renderizar.
 *
 * REGRAS QUE ESTE ARQUIVO GARANTE:
 *
 * 1. Uma requisição por LOTE, nunca por clique. Três disparos: tempo, contagem e tamanho.
 * 2. O teto de tamanho não é preciosismo — o corpo de requisição `keepalive` é limitado a
 *    ~64 KB, e um buffer sem teto perderia O LOTE INTEIRO no flush de fechamento.
 * 3. Nunca derruba a sessão nem incomoda o usuário: 401 desarma o coletor até o próximo
 *    login, e nenhum erro vira toast (ver `ActivityLogApi`, que não usa o `ApiHandler`).
 * 4. Nada de PII: quem chama passa `screenKey`/`routePattern` já sanitizados
 *    (`utilities/screenIdentity.ts`), e o payload é limitado ao que a lista do desenho permite.
 */

/** Intervalo do flush por tempo. */
const FLUSH_INTERVAL_MS = 10_000;

/** Teto de eventos antes de descarregar. */
const FLUSH_AT_COUNT = 25;

/** Teto do corpo serializado, com folga generosa contra o limite de ~64 KB do keepalive. */
const FLUSH_AT_BYTES = 32_768;

/**
 * Teto duro do buffer. Se o envio estiver falhando e o buffer encher, os eventos MAIS ANTIGOS
 * são descartados — perder o começo de uma sessão é melhor que crescer sem limite na memória
 * da aba de quem está trabalhando.
 */
const MAX_BUFFERED_EVENTS = 500;

/** Chave do id de sessão. `sessionStorage` de propósito: uma aba, uma sessão de navegação. */
const SESSION_KEY = "activity-log-session-id";

/** Espelha `ActivityEventTypeEnum` do backend. Numerado a partir de 1, acrescente no FIM. */
export enum ActivityEventType {
  ScreenView = 1,
  FilterApply = 2,
  ShiftChange = 3,
  FilterReset = 4,
  DayChange = 5,
  /** RESERVADO — nenhuma tela do POC exporta nada hoje. */
  Export = 6,
}

export interface TrackActivityInput {
  eventType: ActivityEventType;
  screenKey: string;
  routePattern: string;
  /** Parâmetros da ação. Serializado aqui; mantenha pequeno e sem dado pessoal. */
  payload?: Record<string, unknown> | null;
}

let buffer: ActivityLogEventPayload[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;
/** Desarmado após 401 — sem isso o coletor insistiria a cada 10 s com sessão morta. */
let disabled = false;
let sending = false;
/**
 * Época da sessão de coleta. Incrementada em todo reset/stop.
 *
 * Existe porque um flush em voo escreve estado de MÓDULO na continuação, depois do await — e
 * nesse intervalo a sessão pode ter trocado. Cenário real: flush A pendurado → usuário faz
 * logout → loga de novo na mesma aba (resetActivityLog rearma) → só então chega o 401 de A e a
 * continuação refaz `disabled = true`, matando a telemetria de uma sessão nova e válida.
 *
 * Comparando a época capturada antes do await, a continuação de um flush obsoleto não escreve nada.
 */
let epoch = 0;

/** Id de sessão de navegação, estável enquanto a aba viver. */
const resolveSessionId = (): string => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) return stored;

    const created = generateId();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    // sessionStorage indisponível (aba privada, storage bloqueado): mantém em memória.
    memorySessionId = memorySessionId ?? generateId();
    return memorySessionId;
  }
};

let memorySessionId: string | null = null;

const generateId = (): string => {
  // randomUUID não existe em contexto não-seguro nem no jsdom antigo — o fallback não precisa
  // ser criptográfico, só distinguir abas.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const approximateBytes = (events: ActivityLogEventPayload[]): number => {
  try {
    return JSON.stringify(events).length;
  } catch {
    return 0;
  }
};

const clearTimer = (): void => {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
};

const scheduleFlush = (): void => {
  if (timer !== null || disabled) return;
  timer = setTimeout(() => {
    timer = null;
    void flushActivityLog();
  }, FLUSH_INTERVAL_MS);
};

/**
 * Descarrega o buffer. Nunca lança.
 *
 * Um flush em andamento não é reentrante: um segundo disparo enquanto o primeiro está no ar
 * apenas retorna, e o que sobrou no buffer sai no disparo seguinte.
 */
export const flushActivityLog = async (): Promise<void> => {
  if (disabled || sending || buffer.length === 0) return;

  const batch: ActivityLogBatchPayload = {
    sessionId: resolveSessionId(),
    events: buffer,
  };

  // Esvazia ANTES de enviar: se novos eventos chegarem durante o request, entram no próximo
  // lote em vez de serem enviados duas vezes.
  buffer = [];
  clearTimer();
  sending = true;
  const myEpoch = epoch;

  try {
    const outcome = await ActivityLogApi.SendBatch(batch);

    // A sessão trocou enquanto este lote estava no ar: a continuação não pode mais escrever
    // estado, ou desarmaria a coleta da sessão nova.
    if (myEpoch !== epoch) return;

    if (outcome === "unauthorized") {
      // Sessão morta. Desarma até o próximo login — insistir só geraria 401 em série.
      disabled = true;
      return;
    }
    // "dropped": o lote se perde de propósito. Telemetria não vale uma fila de retry que
    // competiria com o tráfego real da aplicação.
  } finally {
    if (myEpoch === epoch) {
      sending = false;
      if (buffer.length > 0) scheduleFlush();
    }
  }
};

/** Flush de fechamento — precisa ser síncrono, sem await. */
const flushOnUnload = (): void => {
  if (disabled || buffer.length === 0) return;

  const batch: ActivityLogBatchPayload = {
    sessionId: resolveSessionId(),
    events: buffer,
  };
  buffer = [];
  clearTimer();

  ActivityLogApi.SendBatchOnUnload(batch);
};

/**
 * Handlers de fechamento.
 *
 * `pagehide` é o gancho confiável (o `beforeunload` não dispara em toda saída no mobile), e
 * `visibilitychange` cobre a troca de aba, que no celular costuma anteceder o descarte da página.
 *
 * ORDEM IMPORTA: o `AuthContext` agenda logout automático 4 s depois de a última aba fechar e
 * desregistra a aba no `beforeunload`. Registrar em `pagehide` mantém este flush no mesmo
 * momento do descarte, e o backend tolera um lote que chegue com sessão recém-encerrada.
 */
const attachListeners = (): void => {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;

  window.addEventListener("pagehide", flushOnUnload);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnUnload();
  });
};

/**
 * Enfileira um evento. Chamada de handlers de interação — nunca de dentro de `load()`, que
 * roda no mount e a cada push do SignalR e produziria centenas de eventos falsos por hora.
 */
export const trackActivity = (input: TrackActivityInput): void => {
  if (disabled) return;

  attachListeners();

  let payload: string | null = null;
  if (input.payload) {
    try {
      payload = JSON.stringify(input.payload);
    } catch {
      payload = null;
    }
  }

  buffer.push({
    eventType: input.eventType,
    screenKey: input.screenKey,
    routePattern: input.routePattern,
    payload,
    occurredAt: new Date().toISOString(),
  });

  if (buffer.length > MAX_BUFFERED_EVENTS) {
    buffer = buffer.slice(buffer.length - MAX_BUFFERED_EVENTS);
  }

  if (buffer.length >= FLUSH_AT_COUNT || approximateBytes(buffer) >= FLUSH_AT_BYTES) {
    void flushActivityLog();
    return;
  }

  scheduleFlush();
};

/**
 * Rearma o coletor e começa uma sessão nova. Chamado no login.
 * Sem isso, um 401 desarmaria a telemetria pelo resto da vida da aba.
 */
export const resetActivityLog = (): void => {
  epoch += 1;
  disabled = false;
  // Zerado junto: um flush da época anterior ainda em voo não vai mais mexer nesta flag, e sem
  // isto ele bloquearia todo flush novo (o guard de reentrância no topo de flushActivityLog).
  sending = false;
  buffer = [];
  clearTimer();
  memorySessionId = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignorado: sem sessionStorage o id cai no de memória, já limpo acima.
  }
};

/** Descarta o que está no buffer e desarma. Chamado no logout. */
export const stopActivityLog = (): void => {
  epoch += 1;
  sending = false;
  buffer = [];
  clearTimer();
  disabled = true;
};

/** Somente para teste. */
export const __getActivityBufferSize = (): number => buffer.length;
